import React, { useState, useMemo } from "react";
import {
  alpha,
  Avatar,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import LogoutIcon from "@mui/icons-material/Logout";

import { getFullName, getUserDetails, stringAvatar } from "../utils/helpers";
import { DRAWER_WIDTH } from "../utils/constants";
import SideBarContent from "./components/SideBarContent";
import { logoutUser } from "../Redux/authSlice";
import NavItem from "./components/NavItem"; // Our new component
import { Logout } from "@mui/icons-material";

const AppSideNav = ({ isOpen, onClose, isMobile }) => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = getUserDetails();

  const allowedRoutes = useMemo(
    () =>
      SideBarContent.filter(
        (el) => el.roles.includes("all") || el.roles.includes(user.role),
      ),
    [user.role],
  );

  const currentPathKey = useMemo(() => {
    const getActiveKey = () => {
      for (const item of allowedRoutes) {
        // Check if the item itself is the active path
        if (item.path && item.path === location.pathname) {
          return item.key;
        }
        // Check if one of the sub-routes is the active path
        if (item.routes) {
          if (item.routes.some((sub) => sub.path === location.pathname)) {
            return item.key;
          }
        }
      }
      return ""; // No active key found
    };
    return getActiveKey();
  }, [allowedRoutes, location.pathname]);

  const [expandedKey, setExpandedKey] = useState(currentPathKey);

  const handleItemClick = (key, path) => {
    if (path) {
      navigate(path);
      if (isMobile) onClose();
    } else {
      setExpandedKey((prevKey) => (prevKey === key ? "" : key));
    }
  };

  const handleSubItemClick = (path) => {
    navigate(path);
    if (isMobile) onClose();
  };

  const onLogout = () => {
    dispatch(logoutUser());
    navigate("/login");
  };

  const fullName = [user?.first_name, user?.surname].filter(Boolean).join(" ");

  const drawerContent = (
    <>
      <Box
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
        }}
      >
        <Avatar
          {...stringAvatar(`${user.first_name} ${user.surname}`)}
          sx={{
            width: 52,
            height: 52,
            boxShadow: theme.shadows[3],
            border: `2px solid ${theme.palette.background.paper}`,
            bgcolor: theme.palette.primary.main, // Explicitly use primary for avatar bg
            color: theme.palette.primary.contrastText,
          }}
        />
        <Stack sx={{ pl: 2, flexGrow: 1 }}>
          <Typography fontWeight="bold">{fullName}</Typography>
          <Typography variant="body2" color="text">
            {user.role}
          </Typography>
        </Stack>
        {/* <IconButton
          title="Logout"
          onClick={onLogout}
          sx={{ color: theme.palette.secondary.dark }}
        >
          <LogoutIcon />
        </IconButton> */}
      </Box>

      <Divider sx={{ mx: 3, mb: 2, borderColor: theme.palette.divider }} />

      <Box sx={{ flexGrow: 1, overflowY: "auto", px: 1 }}>
        <List component="nav" sx={{ p: 2 }}>
          {allowedRoutes.map((item) => (
            <NavItem
              key={item.key}
              item={item}
              isActive={currentPathKey === item.key}
              isExpanded={expandedKey === item.key}
              onClick={() => handleItemClick(item.key, item.path)}
              onSubItemClick={handleSubItemClick}
            />
          ))}
        </List>
      </Box>
      {/* 
      <Divider
        sx={{
          mx: 3,
          mb: 2,
          border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
        }}
      /> */}

      {/* Footer / Logout */}
      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            p: 2,
            borderRadius: 3,
            // backgroundColor: alpha(theme.palette.error.main, 0.05),
            // border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
          }}
        >
          <ListItemButton
            onClick={onLogout}
            sx={{
              borderRadius: 2,
              color: theme.palette.error.main,
              "&:hover": {
                backgroundColor: alpha(theme.palette.error.main, 0.2),
              },
              p: 1,
            }}
          >
            <ListItemIcon
              sx={{ minWidth: 36, color: theme.palette.error.main }}
            >
              <Logout fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Log Out"
              primaryTypographyProps={{
                variant: "body2",
                fontWeight: 600,
              }}
            />
          </ListItemButton>
        </Box>
      </Box>
    </>
  );

  return (
    <Drawer
      open={isOpen}
      onClose={onClose}
      variant={isMobile ? "temporary" : "persistent"}
      anchor="left"
      sx={{
        width: DRAWER_WIDTH,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: DRAWER_WIDTH,
          boxSizing: "border-box",
          background: theme.palette.background.neutral,
          borderRight: `1px solid ${theme.palette.divider}`,
          color: theme.palette.common,
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default AppSideNav;
